import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Slider, SliderProps, Alert, Button } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { ShadowInfo } from '../types';

// Helper function to format time display from hour value
function formatTimeDisplay(hourValue: number): string {
  const hours = Math.floor(hourValue);
  const minutes = Math.round((hourValue - hours) * 60);
  const period = hours < 12 ? 'AM' : 'PM';
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

interface Mark {
  value: number;
  label: string;
  isOptimal?: boolean;
  isStart?: boolean;
  isEnd?: boolean;
}

interface SliderCustomTrackProps {
  children?: React.ReactNode;
}

// Helper function to get opposite cardinal direction
const getOppositeCardinalDirection = (direction: string): string => {
  const opposites: { [key: string]: string } = {
    'N': 'S',
    'NE': 'SW',
    'E': 'W',
    'SE': 'NW',
    'S': 'N',
    'SW': 'NE',
    'W': 'E',
    'NW': 'SE'
  };
  return opposites[direction] || direction;
};

// Helper function to format cardinal direction pair
const formatCardinalDirectionPair = (direction: string): string => {
  return `${direction}-${getOppositeCardinalDirection(direction)}`;
};

interface ShadowProjectionProps {
  shadowInfo: ShadowInfo | null;
  rowDirection: number;
  rowDistance: number;
  plantHeight: number;
  time: Date;
  onTimeChange: (time: Date, isMoving?: boolean) => void;
  optimalTimeWindows: { start: string; end: string; duration_minutes?: number }[] | null;
  isSliderMoving?: boolean;
  onSaveClick?: () => void;
  isSavedLocation?: boolean;
  locationName?: string;
  onNameChange?: (name: string) => void;
}

// Helper function to convert angle to cardinal direction
const getCardinalDirection = (angle: number): string => {
  // First normalize to 0-360 range
  let normalizedAngle = angle % 360;
  if (normalizedAngle < 0) {
    normalizedAngle += 360;
  }
  
  // Define cardinal directions with their angle ranges
  const directions = [
    { start: 337.5, end: 22.5, dir: "N" },
    { start: 22.5, end: 67.5, dir: "NE" },
    { start: 67.5, end: 112.5, dir: "E" },
    { start: 112.5, end: 157.5, dir: "SE" },
    { start: 157.5, end: 202.5, dir: "S" },
    { start: 202.5, end: 247.5, dir: "SW" },
    { start: 247.5, end: 292.5, dir: "W" },
    { start: 292.5, end: 337.5, dir: "NW" }
  ];
  
  // Find the matching direction
  for (const { start, end, dir } of directions) {
    if (normalizedAngle >= start && normalizedAngle < end) {
      return dir;
    }
  }
  return "N"; // Default to N for 337.5-360 range
};

const ShadowProjection: React.FC<ShadowProjectionProps> = ({
  shadowInfo,
  rowDirection,
  rowDistance,
  plantHeight,
  time,
  onTimeChange,
  optimalTimeWindows,
  isSliderMoving = false,
  onSaveClick,
  isSavedLocation = false,
  locationName = '',
  onNameChange
}) => {
  // Track the last committed shadow info (after slider release) for status display
  const [committedShadowInfo, setCommittedShadowInfo] = useState<ShadowInfo | null>(shadowInfo);

  // Update committed shadow info when shadowInfo changes and slider is not moving
  useEffect(() => {
    if (!isSliderMoving && shadowInfo) {
      setCommittedShadowInfo(shadowInfo);
    }
  }, [shadowInfo, isSliderMoving]);

  // Convert time to slider value (0-24 hours)
  const timeToSliderValue = (time: Date): number => {
    return time.getHours() + time.getMinutes() / 60;
  };

  // Convert slider value to time
  const sliderValueToTime = (value: number): Date => {
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);
    const newTime = new Date(time);
    newTime.setHours(hours, minutes, 0, 0);
    return newTime;
  };

  // Handle slider movement (continuous updates during sliding)
  const handleSliderMove = (_event: Event, value: number | number[]) => {
    if (typeof value === 'number') {
      // Use the onTimeChange with isMoving=true to indicate slider is moving
      onTimeChange(sliderValueToTime(value), true);
    }
  };

  // Handle slider release (final update when slider is released)
  const handleSliderChangeCommitted = (_event: Event | React.SyntheticEvent, value: number | number[]) => {
    if (typeof value === 'number') {
      // Use the onTimeChange with isMoving=false to indicate slider is released
      onTimeChange(sliderValueToTime(value), false);
    }
  };

  // Calculate canvas dimensions - FIXED: Make canvas square to prevent angle distortion
  const canvasSize = 230; // Reduced from 250 to make more space for the slider
  const canvasWidth = canvasSize;
  const canvasHeight = canvasSize;
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;

  // Define scale factor - how many cm in real world equals 1 pixel on screen
  // Increased scale factor to make rows appear closer together visually
  const scaleFactor = rowDistance / 60; // Changed from 100 to 60 to make rows closer together
  
  // Pixel distance between rows (scaled)
  const rowPixelDistance = rowDistance / scaleFactor;

  // Calculate shadow
  const shadowLength = shadowInfo?.shadow_length 
    ? shadowInfo.shadow_length / scaleFactor // Scale shadow length to match row distance scale
    : 0;
  const shadowDirection = shadowInfo?.shadow_direction || 0;
  
  // Define row positions relative to center (before rotation)
  const rowOffsets = [-rowPixelDistance, 0, rowPixelDistance];
  
  // SIMPLEST POSSIBLE APPROACH:
  // 1. Get row direction in radians (negated to reverse rotation)
  // 2. Calculate offsets perpendicular to row direction
  // 3. Draw rows along row direction
  
  // At 0°, rows should be vertical (North-South)
  // For correct rotation: positive angles should rotate clockwise
  const rowAngleRad = (rowDirection + 270) * Math.PI / 180;
  
  // Shadow angle conversion for standard SVG coordinates
  const standardShadowDirection = (90 - shadowDirection + 360) % 360;
  const shadowDirectionRad = standardShadowDirection * Math.PI / 180;
  
  // Calculate row endpoints and coordinates for each row
  const rows = rowOffsets.map(offset => {
    // For perpendicular offset, rotate rowAngle by 90 degrees
    const perpAngleRad = rowAngleRad + Math.PI / 2;
    
    // Apply offset perpendicular to row direction
    const offsetX = offset * Math.cos(perpAngleRad);
    const offsetY = offset * Math.sin(perpAngleRad);
    
    // Row length
    const rowLength = Math.min(canvasWidth, canvasHeight) * 0.4;
    
    // Calculate endpoints along row direction
    return {
      x1: centerX + offsetX - rowLength * Math.cos(rowAngleRad),
      y1: centerY + offsetY - rowLength * Math.sin(rowAngleRad),
      x2: centerX + offsetX + rowLength * Math.cos(rowAngleRad),
      y2: centerY + offsetY + rowLength * Math.sin(rowAngleRad)
    };
  });
  
  // Invert the shadow direction to correct north/south orientation
  const shadowEndX = centerX + shadowLength * Math.cos(shadowDirectionRad);
  const shadowEndY = centerY - shadowLength * Math.sin(shadowDirectionRad);

  // Format time marks for the slider
  const timeMarks = [
    { value: 6, label: '6 AM' },
    { value: 12, label: '12 PM' },
    { value: 18, label: '6 PM' }
  ];

  // Helper function to parse time strings (HH:MM format) to slider values
  const parseTimeString = (timeStr: string): number => {
    try {
      const [hours, minutes] = timeStr.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes)) {
        console.error(`Invalid time format: ${timeStr}`);
        return 0; // Default to midnight
      }
      return hours + (minutes / 60);
    } catch (error) {
      console.error(`Error parsing time string "${timeStr}":`, error);
      return 0; // Default to midnight
    }
  };

  // Safely process optimal time ranges with error handling
  const optimalTimeRanges = React.useMemo(() => {
    try {
      // Check if optimalTimeWindows is an array before calling map
      if (!optimalTimeWindows || !Array.isArray(optimalTimeWindows)) {
        console.warn("optimalTimeWindows is not an array:", optimalTimeWindows);
        return [];
      }
      
      return optimalTimeWindows.map(window => {
        return {
          start: parseTimeString(window.start),
          end: parseTimeString(window.end)
        };
      });
    } catch (error) {
      console.error("Error processing optimal time windows:", error);
      return [];
    }
  }, [optimalTimeWindows]);

  // Safely create optimal marks with error handling
  const optimalMarks: Mark[] = React.useMemo(() => {
    try {
      // Check if optimalTimeWindows is an array before calling map
      if (!optimalTimeWindows || !Array.isArray(optimalTimeWindows)) {
        return [];
      }
      
      return optimalTimeWindows.map(window => {
        const startValue = parseTimeString(window.start);
        const endValue = parseTimeString(window.end);
        
        return [
          { 
            value: startValue, 
            label: formatTimeDisplay(startValue),
            isOptimal: true,
            isStart: true
          },
          { 
            value: endValue, 
            label: formatTimeDisplay(endValue),
            isOptimal: true,
            isEnd: true
          }
        ];
      }).flat();
    } catch (error) {
      console.error("Error creating optimal marks:", error);
      return [];
    }
  }, [optimalTimeWindows]);

  // Combine all marks
  const allMarks: Mark[] = [...timeMarks, ...optimalMarks];

  // Custom track styling for the slider
  const SliderCustomTrack = (props: SliderCustomTrackProps) => {
    const { children, ...other } = props;
    
    return (
      <React.Fragment>
        {/* Create a custom rail that replaces the default one */}
        <div
          style={{
            position: 'absolute',
            height: '6px',
            width: '100%',
            backgroundColor: '#E2E8F0',
            borderRadius: 3,
            opacity: 0.7,
            zIndex: 0
          }}
        />
        
        {/* We need to keep some children for the slider to function properly */}
        {React.Children.map(children, child => {
          if (React.isValidElement(child) && child.props.className?.includes('MuiSlider-track')) {
            // Don't render the track element
            return null;
          }
          return child;
        })}
        
        {/* Render optimal time windows */}
        {optimalTimeRanges.map((range, index) => (
          <React.Fragment key={index}>
            {/* Optimal window with glow effect */}
            <div
              style={{
                position: 'absolute',
                height: '6px',
                width: `${((range.end - range.start) / (20 - 6)) * 100}%`,
                left: `${((range.start - 6) / (20 - 6)) * 100}%`,
                backgroundColor: '#38A169',
                borderRadius: 3,
                opacity: 0.9,
                zIndex: 1,
                boxShadow: '0 0 10px 2px rgba(56, 161, 105, 0.6)',
                top: '11px'
              }}
            />
            {/* Time label for optimal window */}
            {optimalTimeWindows && index < optimalTimeWindows.length && (
              <div
                style={{
                  position: 'absolute',
                  top: '-24px',
                  left: `calc(${((range.start - 6) / (20 - 6)) * 100}% + ${((range.end - range.start) / (20 - 6)) * 50}%)`,
                  transform: 'translateX(-50%)',
                  color: '#38A169',
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  whiteSpace: 'nowrap',
                  zIndex: 2,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}
              >
                {formatTimeDisplay(range.start)} - {formatTimeDisplay(range.end)}
              </div>
            )}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  };

  // Check if current time is in an optimal window
  const isCurrentTimeOptimal = optimalTimeRanges.some(
    range => timeToSliderValue(time) >= range.start && timeToSliderValue(time) <= range.end
  );

  // Check for data loading errors
  const hasError = shadowInfo?.error || (optimalTimeWindows === null && optimalTimeRanges.length === 0);

  return (
    <Paper elevation={3} sx={{ 
      p: 2, 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(to bottom, #ffffff, #f9fafb)',
      borderRadius: 2
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e2e8f0',
        pb: 1,
        mb: 1
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a365d' }}>
          Shadow Projection
        </Typography>
        
        {/* Location name input between title and button */}
        {isSavedLocation && (
          <input
            type="text"
            value={locationName}
            onChange={(e) => onNameChange && onNameChange(e.target.value)}
            placeholder="Location name"
            style={{
              border: 'none',
              background: 'transparent',
              textAlign: 'center',
              fontSize: '0.9rem',
              padding: '4px 8px',
              outline: 'none',
              fontWeight: 600,
              color: '#2D3748',
              flex: '1',
              marginLeft: '8px',
              marginRight: '8px',
              maxWidth: '180px'
            }}
          />
        )}
        
        {onSaveClick && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={onSaveClick}
            size="small"
          >
            {isSavedLocation ? 'Update Location' : 'Save Location'}
          </Button>
        )}
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5, px: 2 }}>
        <Box sx={{ 
          flex: '0 0 auto',
          position: 'relative',
          width: canvasWidth,
          height: canvasHeight,
          mr: 6,
          ml: `${Math.round(canvasWidth * 0.06)}px`
        }}>
          {/* Grid background */}
          <svg width={canvasWidth} height={canvasHeight} style={{ position: 'absolute', top: 0, left: 0 }}>
            {Array.from({ length: 11 }).map((_, i) => (
              <line 
                key={`grid-h-${i}`}
                x1={0}
                y1={i * (canvasHeight / 10)}
                x2={canvasWidth}
                y2={i * (canvasHeight / 10)}
                stroke="#f0f0f0"
                strokeWidth={1}
              />
            ))}
            {Array.from({ length: 11 }).map((_, i) => (
              <line 
                key={`grid-v-${i}`}
                x1={i * (canvasWidth / 10)}
                y1={0}
                x2={i * (canvasWidth / 10)}
                y2={canvasHeight}
                stroke="#f0f0f0"
                strokeWidth={1}
              />
            ))}
          </svg>

          {/* Display error message overlay on the visualization */}
          {hasError && (
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                zIndex: 2
              }}
            >
              <Alert 
                severity="warning" 
                sx={{ 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)', 
                  maxWidth: '90%'
                }}
              >
                {shadowInfo?.error || "Unable to load shadow data. Please try adjusting your parameters."}
              </Alert>
            </Box>
          )}

          <svg width={canvasWidth} height={canvasHeight} style={{ position: 'relative', zIndex: 1 }}>
            {/* Draw rows */}
            <g>
              {rows.map((row, index) => (
                <line 
                  key={index}
                  x1={row.x1}
                  y1={row.y1}
                  x2={row.x2}
                  y2={row.y2}
                  stroke="#718096"
                  strokeWidth={2}
                />
              ))}
              
              {/* Draw plant */}
              <circle 
                cx={centerX} 
                cy={centerY} 
                r={5} 
                fill="#38A169" 
              />
              
              {/* Draw shadow line - use shadowInfo for visual positioning, but color based on committed status */}
              {shadowInfo && !shadowInfo.error && (
                <>
                  <line 
                    x1={centerX} 
                    y1={centerY} 
                    x2={shadowEndX} 
                    y2={shadowEndY} 
                    stroke={isSliderMoving ? "#718096" : (committedShadowInfo?.meets_all_constraints ? "#38A169" : "#E53E3E")}
                    strokeWidth={3}
                    strokeLinecap="round"
                  />
                  <circle 
                    cx={shadowEndX} 
                    cy={shadowEndY} 
                    r={3} 
                    fill={isSliderMoving ? "#718096" : (committedShadowInfo?.meets_all_constraints ? "#38A169" : "#E53E3E")} 
                  />
                </>
              )}
            </g>
          </svg>
        </Box>
        
        <Box sx={{ 
          flex: '1',
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center',
          backgroundColor: '#f7fafc',
          borderRadius: 1,
          p: 1.5,
          ml: 4,
          mr: `${Math.round(canvasWidth * 0.06)}px`,
          minWidth: '270px',
          maxWidth: '330px'
        }}>
          {shadowInfo && !shadowInfo.error ? (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.9rem' }}>
                Shadow Information
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                  fontSize: '0.8rem'
                }}
              >
                <span style={{ fontWeight: 500 }}>Row Direction:</span> 
                <span style={{ fontWeight: 600 }}>
                  {rowDirection.toFixed(1)}° ({formatCardinalDirectionPair(getCardinalDirection(rowDirection))})
                </span>
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                  fontSize: '0.8rem'
                }}
              >
                <span style={{ fontWeight: 500 }}>Sun Elevation:</span> 
                <span style={{ fontWeight: 600 }}>{shadowInfo.sun_elevation?.toFixed(1)}°</span>
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                  fontSize: '0.8rem'
                }}
              >
                <span style={{ fontWeight: 500 }}>Sun Azimuth:</span> 
                <span style={{ fontWeight: 600 }}>{shadowInfo.sun_azimuth?.toFixed(1)}° ({getCardinalDirection(shadowInfo.sun_azimuth || 0)})</span>
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                  fontSize: '0.8rem'
                }}
              >
                <span style={{ fontWeight: 500 }}>Shadow Length:</span> 
                <span style={{ fontWeight: 600 }}>{shadowInfo.shadow_length?.toFixed(1)} cm</span>
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                  fontSize: '0.8rem'
                }}
              >
                <span style={{ fontWeight: 500 }}>Angle from Row:</span> 
                <span style={{ fontWeight: 600 }}>{shadowInfo.angle_from_row?.toFixed(1)}°</span>
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                  fontSize: '0.8rem'
                }}
              >
                <span style={{ fontWeight: 500 }}>Distance from Row:</span> 
                <span style={{ fontWeight: 600 }}>{shadowInfo.perpendicular_distance?.toFixed(1)} cm</span>
              </Typography>
              
              {/* Only show the status box if not currently sliding */}
              {!isSliderMoving && committedShadowInfo && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 0.25,
                    backgroundColor: committedShadowInfo.meets_all_constraints ? 'rgba(56, 161, 105, 0.1)' : 'rgba(229, 62, 62, 0.1)',
                    borderRadius: 1,
                    mt: 0.25,
                    fontSize: '0.8rem'
                  }}
                  color={committedShadowInfo.meets_all_constraints ? "success.main" : "error.main"}
                >
                  <span style={{ fontWeight: 500 }}>Status:</span> 
                  <span style={{ 
                    fontWeight: 600,
                    display: 'inline-block',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    backgroundColor: committedShadowInfo.meets_all_constraints ? 'rgba(56, 161, 105, 0.2)' : 'rgba(229, 62, 62, 0.2)',
                  }}>
                    {committedShadowInfo.meets_all_constraints ? "Optimal" : "Not Optimal"}
                  </span>
                </Typography>
              )}
              
              {/* Show a placeholder status box when slider is moving */}
              {isSliderMoving && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 0.25,
                    backgroundColor: 'rgba(160, 174, 192, 0.1)',
                    borderRadius: 1,
                    mt: 0.25,
                    fontSize: '0.8rem'
                  }}
                  color="text.secondary"
                >
                  <span style={{ fontWeight: 500 }}>Status:</span> 
                  <span style={{ 
                    fontWeight: 600,
                    display: 'inline-block',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(160, 174, 192, 0.2)',
                  }}>
                    Calculating...
                  </span>
                </Typography>
              )}
            </>
          ) : (
            <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              Shadow information not available
            </Typography>
          )}
        </Box>
      </Box>
      
      {/* Time slider */}
      <Box sx={{ mt: 1, mb: isCurrentTimeOptimal ? 1 : 2, px: `calc(2rem + ${Math.round(canvasWidth * 0.06)}px)` }}>
        <Typography id="time-slider" gutterBottom sx={{ 
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
          fontSize: '0.85rem'
        }}>
          <span>Time: {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          {/* Only show optimal time badge when not sliding */}
          {isCurrentTimeOptimal && !isSliderMoving && (
            <span style={{ 
              fontSize: '11px',
              color: '#38A169',
              backgroundColor: 'rgba(56, 161, 105, 0.1)',
              padding: '1px 6px',
              borderRadius: '4px',
              fontWeight: 600
            }}>
              OPTIMAL TIME
            </span>
          )}
        </Typography>
        
        {/* Current time indicator */}
        <Box sx={{ 
          position: 'relative', 
          height: 24, 
          mb: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Box sx={{ 
            position: 'absolute',
            left: `${((timeToSliderValue(time) - 6) / (20 - 6)) * 100}%`,
            transform: 'translateX(-50%)',
            backgroundColor: isCurrentTimeOptimal ? '#38A169' : '#3182CE',
            color: 'white',
            py: 0.25,
            px: 0.75,
            borderRadius: 0.75,
            fontSize: '0.8rem',
            fontWeight: 'bold',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            zIndex: 2
          }}>
            {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Box>
        </Box>
        
        <Slider
          aria-labelledby="time-slider"
          value={timeToSliderValue(time)}
          min={6}
          max={20}
          step={0.25}
          onChange={handleSliderMove}
          onChangeCommitted={handleSliderChangeCommitted}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => {
            const time = sliderValueToTime(value);
            return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          }}
          marks={[
            { value: 6, label: '6 AM' },
            { value: 12, label: '12 PM' },
            { value: 18, label: '6 PM' }
          ]}
          track={false}
          components={{ Track: SliderCustomTrack }}
          sx={{
            height: 6,
            '& .MuiSlider-track': {
              display: 'none',
              backgroundColor: 'transparent',
              border: 'none'
            },
            '@keyframes pulse': {
              '0%': {
                boxShadow: '0 0 10px 2px rgba(56, 161, 105, 0.4)',
              },
              '50%': {
                boxShadow: '0 0 14px 4px rgba(56, 161, 105, 0.6)',
              },
              '100%': {
                boxShadow: '0 0 10px 2px rgba(56, 161, 105, 0.4)',
              }
            },
            '& .MuiSlider-markLabel': {
              fontSize: '0.65rem',
              color: '#4A5568',
              fontWeight: 500,
              "&[data-index='1']": { // Highlight noon label
                color: '#2D3748',
                fontWeight: 600
              }
            },
            '& .MuiSlider-mark, & .MuiSlider-markActive': {
              backgroundColor: 'transparent',
              height: 0,
              width: 0,
              marginTop: 0,
              display: 'none' // Added to ensure marks are completely hidden
            },
            '& .MuiSlider-thumb': {
              width: 16,
              height: 16,
              backgroundColor: isCurrentTimeOptimal ? '#38A169' : '#3182CE',
              border: '2px solid white',
              boxShadow: '0 0 3px rgba(0,0,0,0.3)',
              zIndex: 2,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: isCurrentTimeOptimal 
                  ? '0 0 0 6px rgba(56, 161, 105, 0.16)' 
                  : '0 0 0 6px rgba(49, 130, 206, 0.16)',
              }
            },
            '& .MuiSlider-rail': {
              display: 'none' // Hide the default rail since we're creating our own
            }
          }}
        />
      </Box>
    </Paper>
  );
};

export default ShadowProjection; 